import {DOCUMENT} from '@angular/common';
import {Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, Output, Renderer2} from '@angular/core';
import {animate, style, transition, trigger} from '@angular/animations';
import {ISelectOption} from '../../../../../shared-models/custom-select/custom-select';
import {getDeepCopyOfObject, isNonEmptyObject} from '../../../../../shared-utils/object/object.utils';
import {IconColourEnum} from '../../../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {KeyboardBindingEnum} from '../../../../../utils/keyboard/key-codes';
import {IconsEnum} from '../../../../../../shared-modules/shared-icons/models/icons.enum';
import {IStore} from '../../../../../shared-models/store/store';

// Todo: Technical Debt: Deprecated symbol used, consult docs for better alternative
@Component({
  selector: 'app-shared-custom-select',
  templateUrl: './shared-custom-select.component.html',
  styleUrls: ['./shared-custom-select.component.scss'],
  animations: [
    trigger('dropdownAnimation', [
      transition(':enter', [
        style({opacity: 0, transform: 'scaleY(0.8)'}),
        animate('200ms ease-out', style({opacity: 1, transform: 'scaleY(1)'})),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({opacity: 0, transform: 'scaleY(0.8)'})),
      ]),
    ]),
  ],
  standalone: false,
})
export class SharedCustomSelectComponent implements OnDestroy {

  @Input() placeholder = 'Select';
  @Input() searchPlaceholder = 'Search...';
  @Input() maxSelections?: number;
  @Input() maxLength?: number;
  @Input() formatSearchText?: (text: string) => string;
  @Input() showChips?: boolean = true;
  @Input() allowMultiple = true;
  @Input() allowSelectAll = true;
  @Input() showSearchBar = true;
  @Input() showToolTips = false;
  @Input() toolTips: Record<string, string> = {};
  @Input() isDisabled = false;
  @Input() showTitle = false;
  @Input() setIcon?: IconsEnum = IconsEnum.eyeOutline;
  @Input() setIconColour?: IconColourEnum;
  @Input() setDefault?: Record<string, ISelectOption>;
  @Input() saveLoad?: boolean = false;
  @Input() allowAddNew?: boolean = false;
  @Input() autoSubmitOnSelect: boolean = false;
  @Input() closeOnAutoSubmit: boolean = false;
  @Input() preserveOrder: boolean = false;
  @Input() showCheckbox: boolean = true;
  @Input() showStoreIcon: boolean = false;
  @Input() showActionButtons: boolean = true;
  @Input() showFreshnessIndicator: boolean = false;
  @Input() stores: Record<string, IStore> = {};
  @Input() dataTimeStampKey: string[] = [];
  @Input() storesHeaderDropdown: boolean = false;
  @Input() allowEmptySelection: boolean = false;
  @Input() floatDropdown: boolean = false;
  @Input() dropdownMaxHeight: number = 350;

  private _keyOrder?: string[];

  @Output() readonly selectionChange = new EventEmitter<Record<string, ISelectOption>>();
  @Output() readonly saveLoadClick = new EventEmitter<Record<string, ISelectOption>>();

  highlightedIndex = 0;
  isDropdownOpen = false;
  optionKeys: string[] = [];
  options: Record<string, ISelectOption> = {};
  searchText = '';
  selectAllCheckBox = {
    isAllSelected: false,
    isSomeSelected: true,
  };

  selectedArray: string[] = [];
  dropdownPosition: 'above' | 'below' = 'below';

  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons: typeof IconsEnum = IconsEnum;

  private tempOptions: Record<string, ISelectOption> | null = null;
  private cachedSortedKeys: string[] = [];
  private floatingPanel: HTMLElement | null = null;
  private floatingHost: HTMLElement | null = null;
  private removeWindowResizeListener: (() => void) | null = null;

  firstSelectedOption: string;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }


  get keyOrder(): string[] | undefined {
    return this._keyOrder;
  }

  get currentOptions(): Record<string, ISelectOption> {
    return this.tempOptions || this.options;
  }

  get filteredOptionKeys(): string[] {
    // Use cached sorted keys, only filter by search text
    if (this.searchText) {
      return this.cachedSortedKeys.filter((key: string) =>
        this.currentOptions[key].name.toLowerCase().includes(this.searchText.toLowerCase()),
      );
    }
    return this.cachedSortedKeys;
  }

  get selectedCount(): number {
    return this.optionKeys.filter((key: string) => this.options[key].selected).length;
  }

  get isEverySelectableStoreSelected(): boolean {
    if (!this.options) {
      return false;
    }
    const selectableKeys = Object.keys(this.options).filter((key: string) => !this.options[key]?.disabled);
    return selectableKeys.length > 0 && selectableKeys.every((key: string) => this.options[key].selected);
  }

  get headerStoreSelectionLabel(): string {
    if (this.isEverySelectableStoreSelected) {
      return 'All Stores Selected';
    }
    if (this.selectedCount === 1) {
      return this.options?.[this.firstSelectedOption]?.name || '1 Store Selected';
    }
    return `${this.selectedCount} Stores Selected`;
  }

  get getSelectedOptions(): string[] {
    return this.selectedArray;
  }

  get canAddNewOption(): boolean {
    const trimmedSearch = this.searchText.trim();
    return this.allowAddNew && trimmedSearch.length > 0 && !this.currentOptions[trimmedSearch];
  }

  @Input() set keyOrder(value: string[] | undefined) {
    this._keyOrder = value;
    // When keyOrder changes, recalculate if options already exist
    if (this.options) {
      this.updateOptionKeys();
      this.calculateSortedKeys();
    }
  }

  @Input() set setOptions(options: Record<string, ISelectOption>) {
    if (!isNonEmptyObject(options)) return;

    const defaultSelected = Object.keys(options).filter((key: string) => options[key].selected);
    this.setOptionsAndKeys(options);

    if (!this.allowMultiple) {
      const selectedKey = defaultSelected[0] ?? '';
      this.selectedArray = selectedKey ? [selectedKey] : [];
      Object.keys(this.options).forEach((key: string) => {
        this.options[key].selected = key === selectedKey;
      });
      this.firstSelectedOption = selectedKey;
      return;
    }

    const pendingSelected = [...this.selectedArray];

    if (this.allowEmptySelection) {
      this.selectedArray = defaultSelected;
      this.firstSelectedOption = this.selectedArray[0] ?? '';
      return;
    }

    this.selectedArray = pendingSelected.length ? pendingSelected : defaultSelected;

    if (pendingSelected.length) {
      this.setSelected();
    }

    this.firstSelectedOption = this.selectedArray[0] ?? '';
  }

  @Input() set selected(selected: string[]) {

    if (selected.length < 1) {
      if (this.allowEmptySelection) {
        this.clearSelection();
      }
      return;
    }

    this.selectedArray = selected;

    Object.keys(this.options).forEach((key: string) => {
      this.options[key].selected = this.selectedArray.includes(key);
    });

    this.setSelected();
    this.refreshSelectAllCheckbox();
  }

  ngOnDestroy(): void {
    this.releaseFloatingDropdown();
  }

  private readonly onCapturedScroll = (): void => {
    if (this.floatDropdown && this.isDropdownOpen) {
      this.applyFloatingDropdownPosition();
    }
  };

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    const target = event.target as Node | null;
    if (!target || !this.isDropdownOpen) {
      return;
    }
    if (this.elementRef.nativeElement.contains(target) || this.floatingPanel?.contains(target)) {
      return;
    }
    if (this.shouldCommitOnDismiss()) {
      if (this.hasPendingSelectionChange()) {
        this.autoSubmitSelection();
      }
      if (this.isDropdownOpen) {
        this.cancelSelection();
      }
      return;
    }
    this.cancelSelection();
  }

  setSelected(): void {
    this.getSelectedOptions?.forEach((option: string) => {
      if (this.options[option]) {
        this.options[option].selected = true;
        this.firstSelectedOption = option;
      }
    });
  }

  /**
   * Clears the current selection so an explicit empty `selected` input is honoured. Deselects every option
   * and resets the displayed value back to the placeholder.
   */
  private clearSelection(): void {
    this.selectedArray = [];
    Object.keys(this.options).forEach((key: string) => {
      this.options[key].selected = false;
    });
    this.firstSelectedOption = '';
  }

  private calculateSortedKeys(): void {
    const keys = this.optionKeys;

    // If keyOrder is provided, use it directly without grouping by selection status
    if (this.keyOrder && this.keyOrder.length > 0) {
      this.cachedSortedKeys = [...this.keyOrder];
      return;
    }

    // Separate into three groups: selected (not disabled), non-selected (not disabled), and disabled (all)
    const selectedKeys: string[] = [];
    const nonSelectedKeys: string[] = [];
    const disabledKeys: string[] = [];

    keys.forEach((key: string) => {
      const option = this.currentOptions[key];
      if (!option) return;
      if (option.disabled) {
        // All disabled items go to the bottom, regardless of selection status
        disabledKeys.push(key);
      } else if (option.selected) {
        selectedKeys.push(key);
      } else {
        nonSelectedKeys.push(key);
      }
    });

    // Sort each group - use keyOrder order if available, otherwise sort alphabetically
    const sortKeys = (keys: string[]): void => {
      if (this.keyOrder && this.keyOrder.length > 0) {
        // Sort by the order defined in keyOrder
        keys.sort((a: string, b: string) => {
          const indexA = this.keyOrder.indexOf(a);
          const indexB = this.keyOrder.indexOf(b);
          // If either key is not in keyOrder, fall back to alphabetical
          if (indexA === -1 || indexB === -1) {
            return this.currentOptions[a].name.localeCompare(this.currentOptions[b].name);
          }
          return indexA - indexB;
        });
      } else {
        // Fall back to alphabetical sort by name
        keys.sort((a: string, b: string) =>
          this.currentOptions[a].name.localeCompare(this.currentOptions[b].name)
        );
      }
    };

    sortKeys(selectedKeys);
    sortKeys(nonSelectedKeys);
    sortKeys(disabledKeys);

    // Concatenate in the desired order: selected, non-selected, disabled
    this.cachedSortedKeys = [...selectedKeys, ...nonSelectedKeys, ...disabledKeys];
  }

  toggleDropdown(): void {
    this.setSelected();
    if (!this.isDropdownOpen && this.isDisabled === false) {
      this.tempOptions = getDeepCopyOfObject(this.options);
      this.calculateSortedKeys();
      this.calculateDropdownPosition();
      this.isDropdownOpen = true;
      this.searchText = '';
      this.highlightedIndex = Object.keys(this.tempOptions).indexOf(this.selectedArray[0]);
      if (this.floatDropdown) {
        setTimeout((): void => {
          this.mountFloatingDropdown();
          this.applyFloatingDropdownPosition();
          this.queueSearchInputFocus();
        });
      } else {
        setTimeout((): void => {
          this.scrollDropdownIntoView();
          this.queueSearchInputFocus();
        });
      }
    } else {
      this.releaseFloatingDropdown();
      this.isDropdownOpen = false;
    }
  }

  cancelSelection(): void {
    this.tempOptions = null;
    this.releaseFloatingDropdown();
    this.isDropdownOpen = false;
  }

  onDefaultClick(): void {
    this.tempOptions = getDeepCopyOfObject(this.setDefault);
  }

  onSaveLoadClick(): void {
    this.options = this.currentOptions;
    this.updateOptionKeys();
    this.tempOptions = null;
    this.saveLoadClick.emit(this.options);
  }


  confirmSelection(): void {
    if (this.currentOptions) {
      this.options = this.currentOptions;
      this.updateOptionKeys();
      this.selectedArray = Object.keys(this.options).filter(k => this.options[k].selected);
      this.tempOptions = null;
      this.selectionChange.emit(this.options);
    }
    this.tempOptions = null;
    this.releaseFloatingDropdown();
    this.isDropdownOpen = false;
  }

  autoSubmitSelection(): void {
    if (this.currentOptions) {
      this.options = this.currentOptions;
      this.updateOptionKeys();
      this.selectedArray = Object.keys(this.options).filter(k => this.options[k].selected);
      this.firstSelectedOption = this.selectedArray[0] ?? '';
      this.selectionChange.emit(this.options);
    }
    if (this.closeOnAutoSubmit) {
      this.tempOptions = null;
      this.releaseFloatingDropdown();
      this.isDropdownOpen = false;
    }
  }

  /**
   * Single-select menus without confirm buttons apply the pending choice when the user clicks away.
   */
  private shouldCommitOnDismiss(): boolean {
    return !this.allowMultiple && !this.showActionButtons;
  }

  /**
   * Returns true when the open menu's temporary choice differs from the committed selection.
   */
  private hasPendingSelectionChange(): boolean {
    if (!this.tempOptions) {
      return false;
    }

    const pendingKeys = Object.keys(this.tempOptions)
      .filter((key: string) => this.tempOptions?.[key].selected)
      .sort();
    const committedKeys = Object.keys(this.options)
      .filter((key: string) => this.options[key].selected)
      .sort();
    return pendingKeys.join('|') !== committedKeys.join('|');
  }

  /**
   * Keeps the select-all checkbox in step with the current option list.
   */
  private refreshSelectAllCheckbox(): void {
    const options = this.currentOptions || this.options;
    if (!options) {
      this.selectAllCheckBox.isAllSelected = false;
      this.selectAllCheckBox.isSomeSelected = false;
      return;
    }
    const selectableKeys = Object.keys(options).filter((key: string) => !options[key]?.disabled);
    const selectedCount = selectableKeys.filter((key: string) => options[key]?.selected).length;
    this.selectAllCheckBox.isAllSelected = selectableKeys.length > 0 && selectedCount === selectableKeys.length;
    this.selectAllCheckBox.isSomeSelected = selectedCount > 0 && selectedCount < selectableKeys.length;
  }

  onToggleAll(event: Event): void {
    this.options = this.currentOptions;

    this.updateOptionKeys();
    const checked = !!(event.target as HTMLInputElement).checked;
    this.selectAllCheckBox.isAllSelected = checked;
    if (checked) {
      this.selectAllCheckBox.isSomeSelected = false;
    }
    let remaining = this.maxSelections ?? Number.POSITIVE_INFINITY;
    Object.keys(this.currentOptions).forEach((key: string) => {
      if (this.currentOptions[key].disabled !== false) {
        return;
      }
      if (!checked) {
        this.currentOptions[key].selected = false;
        return;
      }
      this.currentOptions[key].selected = remaining > 0;
      if (remaining > 0) {
        remaining -= 1;
      }
    });
    this.refreshSelectAllCheckbox();
    if (this.autoSubmitOnSelect) {
      this.autoSubmitSelection();
    }
  }

  toggleOption(key: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.currentOptions[key].disabled) {
      return;
    }

    if (!this.allowMultiple) {
      if (isNonEmptyObject(this.tempOptions)) {
        Object.keys(this.tempOptions).forEach((k: string) => {
          this.tempOptions[k].selected = false;
        });
        this.tempOptions[key].selected = true;
      }
      this.highlightedIndex = Object.keys(this.tempOptions).indexOf(key);
      this.selectAllCheckBox.isSomeSelected = true;
      this.selectAllCheckBox.isAllSelected = false;

      if (this.autoSubmitOnSelect) {
        this.autoSubmitSelection();
      }
      if (this.storesHeaderDropdown) {
        this.releaseFloatingDropdown();
        this.isDropdownOpen = false;
      }
      return;
    } else {
      const turningOn = !this.currentOptions[key].selected;
      const selectedCount = this.optionKeys.filter(
        (optionKey: string) => this.currentOptions[optionKey]?.selected && !this.currentOptions[optionKey]?.disabled,
      ).length;
      if (turningOn && this.maxSelections && selectedCount >= this.maxSelections) {
        return;
      }
      if (this.tempOptions) {
        this.tempOptions[key].selected = !this.tempOptions[key].selected;
      }
      const currentSelections = this.optionKeys.filter((k: string) => this.currentOptions[k].selected);
      const noneSelected = currentSelections.length === 0;
      const allSelected = currentSelections.length === Object.keys(this.options).length;
      this.selectAllCheckBox.isAllSelected = allSelected;
      this.selectAllCheckBox.isSomeSelected = !noneSelected && !allSelected;
      this.refreshSelectAllCheckbox();

      if (this.autoSubmitOnSelect) {
        this.autoSubmitSelection();
      }
    }
  }

  onSearchChange(): void {
    if (this.formatSearchText) {
      this.searchText = this.formatSearchText(this.searchText);
    }
    this.highlightedIndex = 0;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.isDropdownOpen) {
      return;
    }
    const filteredOptions = this.filteredOptionKeys;
    switch (event.key) {
      case KeyboardBindingEnum.ArrowDown:
        event.preventDefault();
        if (this.highlightedIndex < filteredOptions.length - 1) {
          this.highlightedIndex++;
        }
        break;
      case KeyboardBindingEnum.ArrowUp:
        event.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
        }
        break;
      case KeyboardBindingEnum.Enter:
        event.preventDefault();
        const key = filteredOptions[this.highlightedIndex];
        if (key) {
          this.toggleOption(key, event as unknown as MouseEvent);
        }
        break;
      case KeyboardBindingEnum.Escape:
        event.preventDefault();
        this.cancelSelection();
        break;
    }
  }

  removeChip(key: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.options[key].disabled) {
      return;
    }
    this.options[key].selected = false;
    this.selectionChange.emit(this.options);
  }

  addNewOption(): void {
    const trimmedSearch = this.searchText.trim();
    if (!trimmedSearch || this.currentOptions[trimmedSearch]) {
      return;
    }

    const newOption: ISelectOption = {
      name: trimmedSearch,
      selected: true,
      disabled: false,
    };

    if (this.tempOptions) {
      this.tempOptions[trimmedSearch] = newOption;
    } else {
      this.options[trimmedSearch] = newOption;
    }

    this.options[trimmedSearch] = newOption;
    this.updateOptionKeys();
    this.selectionChange.emit(this.options);

    this.searchText = '';
    this.highlightedIndex = 0;
  }

  private setOptionsAndKeys(options: Record<string, ISelectOption>): void {
    this.options = options;
    this.updateOptionKeys();
    this.calculateSortedKeys();
  }

  private updateOptionKeys(): void {
    if (this.keyOrder && this.keyOrder.length > 0) {
      this.optionKeys = [...this.keyOrder];
    } else if (this.preserveOrder) {
      this.optionKeys = Object.keys(this.options);
    } else {
      this.optionKeys = Object.keys(this.options).sort((a: string, b: string) => {
        const nameA = this.options[a]?.name?.toLowerCase() ?? '';
        const nameB = this.options[b]?.name?.toLowerCase() ?? '';
        return nameA.localeCompare(nameB);
      });
    }
  }

  private calculateDropdownPosition(): void {
    if (this.floatDropdown) {
      this.dropdownPosition = 'below';
      return;
    }

    const element = this.elementRef.nativeElement as HTMLElement;
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = this.dropdownMaxHeight + 100;

    // Prefer opening below so overflow scroll parents can reveal the panel.
    // Only open above when it fully fits above and there is not enough room below.
    const fitsBelow = spaceBelow >= dropdownHeight;
    const fitsAbove = spaceAbove >= dropdownHeight;

    this.dropdownPosition = !fitsBelow && fitsAbove ? 'above' : 'below';
  }

  /**
   * Scrolls the open dropdown into view within the nearest scrollable ancestor.
   */
  private scrollDropdownIntoView(): void {
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    const panel = hostElement.querySelector<HTMLElement>('.dropdown-panel');
    if (!panel) {
      return;
    }

    panel.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
  }

  /**
   * Moves the open panel to document.body so modal transforms and scroll containers cannot shift or clip it.
   */
  private mountFloatingDropdown(): void {
    if (!this.isDropdownOpen || this.floatingPanel) {
      return;
    }

    const hostElement = this.elementRef.nativeElement as HTMLElement;
    const panel = hostElement.querySelector<HTMLElement>('.dropdown-panel');
    if (!panel) {
      return;
    }

    const floatingHost = this.renderer.createElement('div') as HTMLElement;
    floatingHost.className = 'custom-select';
    floatingHost.style.position = 'fixed';
    floatingHost.style.left = '0';
    floatingHost.style.top = '0';
    floatingHost.style.width = '0';
    floatingHost.style.height = '0';
    floatingHost.style.overflow = 'visible';
    floatingHost.style.zIndex = '25000';
    floatingHost.style.pointerEvents = 'none';
    panel.style.pointerEvents = 'auto';
    floatingHost.appendChild(panel);
    this.document.body.appendChild(floatingHost);
    this.floatingHost = floatingHost;
    this.floatingPanel = panel;
    this.document.addEventListener('scroll', this.onCapturedScroll, true);
    this.removeWindowResizeListener = this.renderer.listen('window', 'resize', this.onCapturedScroll);
  }

  /**
   * Returns a portaled panel to the component before Angular destroys the view.
   */
  private releaseFloatingDropdown(): void {
    const panel = this.floatingPanel;
    const floatingHost = this.floatingHost;
    this.floatingPanel = null;
    this.floatingHost = null;
    this.document.removeEventListener('scroll', this.onCapturedScroll, true);
    this.removeWindowResizeListener?.();
    this.removeWindowResizeListener = null;

    if (panel?.isConnected) {
      panel.style.pointerEvents = '';
      const hostElement = this.elementRef.nativeElement as HTMLElement;
      const selectRoot = hostElement.querySelector('.custom-select');
      if (selectRoot && panel.parentElement !== selectRoot) {
        selectRoot.appendChild(panel);
      } else if (panel.parentElement !== selectRoot) {
        panel.remove();
      }
    }

    floatingHost?.remove();
  }

  /**
   * Positions the dropdown panel with viewport coordinates so it is not clipped by scroll containers.
   */
  private applyFloatingDropdownPosition(): void {
    const hostElement = this.elementRef.nativeElement as HTMLElement;
    const panel = this.floatingPanel ?? hostElement.querySelector<HTMLElement>('.dropdown-panel');
    const trigger = hostElement.querySelector<HTMLElement>('.select-display');
    if (!panel || !trigger) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const gap = 4;
    const maxDropdownHeight = this.dropdownMaxHeight;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openAbove = spaceBelow < Math.min(maxDropdownHeight, spaceAbove) && spaceAbove > spaceBelow;

    panel.classList.remove('dropdown-above', 'dropdown-floating-above', 'dropdown-floating-below');
    panel.classList.add('dropdown-floating', 'dropdown-floating-ready', openAbove ? 'dropdown-floating-above' : 'dropdown-floating-below');
    this.dropdownPosition = openAbove ? 'above' : 'below';

    const floatWidth = triggerRect.width;
    panel.style.setProperty('--float-left', `${triggerRect.left}px`);
    panel.style.setProperty('--float-width', `${floatWidth}px`);
    panel.style.removeProperty('--float-top');
    panel.style.removeProperty('--float-bottom');

    if (openAbove) {
      panel.style.setProperty('--float-bottom', `${window.innerHeight - triggerRect.top + gap}px`);
      panel.style.setProperty('--float-max-height', `${Math.max(0, Math.min(maxDropdownHeight, spaceAbove - gap))}px`);
    } else {
      panel.style.setProperty('--float-top', `${triggerRect.bottom + gap}px`);
      panel.style.setProperty('--float-max-height', `${Math.max(0, Math.min(maxDropdownHeight, spaceBelow - gap))}px`);
    }
  }

  /**
   * Focuses the search input after the dropdown panel renders so users can type immediately.
   */
  private queueSearchInputFocus(): void {
    if (!this.showSearchBar) {
      return;
    }

    setTimeout((): void => {
      if (!this.isDropdownOpen) {
        return;
      }

      const hostElement = this.elementRef.nativeElement as HTMLElement;
      const panel = this.floatingPanel ?? hostElement.querySelector<HTMLElement>('.dropdown-panel');
      const searchInput = panel?.querySelector<HTMLInputElement>('.header input[type="text"]');
      searchInput?.focus();
      searchInput?.select();
    });
  }

}
